import { useState } from "react";
import { Check, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import {
  useCreateReimbursement,
  useDeleteReimbursement,
  useReimbursementList,
  useUpdateReimbursement,
} from "../hooks/useExpenses";
import { formatCurrency } from "./CurrencyAmount";
import type { ExpenseDetail, ReimbursementEvent } from "../types";

const BORDER = "hsl(210,30%,85%)";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

type Props = {
  expense: ExpenseDetail;
};

export function ReimbursementCard({ expense }: Props) {
  const { data: events = [], isLoading } = useReimbursementList(expense.id);
  const { data: paymentItems = [] } = useCategoryItems("expense_payment_method");
  const createEvent = useCreateReimbursement(expense.id);
  const updateEvent = useUpdateReimbursement(expense.id);
  const deleteEvent = useDeleteReimbursement(expense.id);

  const [recordOpen, setRecordOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [eventToDelete, setEventToDelete] = useState<ReimbursementEvent | null>(null);

  const total = events.reduce((s, e) => s + e.amount, 0);
  const owed = Math.max(0, expense.amount - total);
  const status: "not_required" | "pending" | "partial" | "complete" =
    !expense.isReimbursable
      ? "not_required"
      : total === 0
        ? "pending"
        : total < expense.amount
          ? "partial"
          : "complete";

  const statusStyle: Record<typeof status, { bg: string; fg: string; label: string }> = {
    not_required: {
      bg: "hsl(210,16%,93%)",
      fg: "hsl(215,16%,40%)",
      label: "Not Reimbursable",
    },
    pending: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "Pending" },
    partial: {
      bg: "hsl(210,55%,92%)",
      fg: "hsl(210,100%,28%)",
      label: "Partial",
    },
    complete: {
      bg: "hsl(142,55%,92%)",
      fg: "hsl(142,72%,28%)",
      label: "Reimbursed",
    },
  };
  const s = statusStyle[status];

  return (
    <div
      className="rounded-xl border bg-white"
      style={{ borderColor: BORDER, padding: "16px 20px" }}
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-foreground">
          Reimbursement
        </h3>
        {expense.isReimbursable && (
          <button
            type="button"
            onClick={() => setRecordOpen(true)}
            className="flex h-7 items-center gap-1 rounded-md border px-2.5 text-[12px] font-medium text-foreground hover:bg-[hsl(210,50%,96%)]"
            style={{ borderColor: BORDER }}
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
            Record Reimbursement
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <span
          className="inline-flex items-center rounded-full px-3 py-1 text-[12px] font-semibold"
          style={{ background: s.bg, color: s.fg }}
        >
          {s.label}
        </span>
        {expense.isReimbursable && (
          <span className="text-[12px] text-muted-foreground">
            <span className="font-semibold text-foreground">
              {formatCurrency(total, expense.currency)}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-foreground">
              {formatCurrency(expense.amount, expense.currency)}
            </span>{" "}
            paid
            {owed > 0 && (
              <>
                {" "}— <span className="text-foreground">
                  {formatCurrency(owed, expense.currency)} owed
                </span>
              </>
            )}
          </span>
        )}
      </div>

      {expense.isReimbursable && (
        <div className="mt-4">
          <div className="mb-2 text-[11px] uppercase text-muted-foreground">
            Payment History
          </div>
          {isLoading ? (
            <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              Loading…
            </div>
          ) : events.length === 0 ? (
            <div className="py-3 text-sm text-muted-foreground">
              No payments recorded yet.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "hsl(210,30%,93%)" }}>
              {events.map((evt) => (
                <li key={evt.id} className="py-2.5">
                  {editingId === evt.id ? (
                    <EditEventRow
                      event={evt}
                      expenseCurrency={expense.currency}
                      paymentItems={paymentItems}
                      onCancel={() => setEditingId(null)}
                      onSave={async (payload) => {
                        try {
                          await updateEvent.mutateAsync({ id: evt.id, payload });
                          setEditingId(null);
                        } catch {
                          // error surfaces via mutation state; row stays open
                        }
                      }}
                      saving={updateEvent.isPending}
                      error={
                        updateEvent.error instanceof Error
                          ? updateEvent.error.message
                          : null
                      }
                    />
                  ) : (
                    <ViewEventRow
                      event={evt}
                      expenseCurrency={expense.currency}
                      onEdit={() => setEditingId(evt.id)}
                      onDelete={() => setEventToDelete(evt)}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {recordOpen && (
        <RecordDialog
          expenseId={expense.id}
          expenseCurrency={expense.currency}
          owed={owed}
          paymentItems={paymentItems}
          onClose={() => setRecordOpen(false)}
          onSubmit={async (payload) => {
            try {
              await createEvent.mutateAsync(payload);
              setRecordOpen(false);
            } catch {
              // error stays visible inside dialog
            }
          }}
          saving={createEvent.isPending}
          error={
            createEvent.error instanceof Error
              ? createEvent.error.message
              : null
          }
        />
      )}

      <ConfirmDialog
        open={eventToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setEventToDelete(null);
        }}
        title="Delete reimbursement?"
        description={
          eventToDelete
            ? `Are you sure you want to delete this ${formatCurrency(eventToDelete.amount, expense.currency)} reimbursement from ${formatDate(eventToDelete.paidDate)}?`
            : ""
        }
        onConfirm={async () => {
          if (!eventToDelete) return;
          await deleteEvent.mutateAsync(eventToDelete.id);
        }}
      />
    </div>
  );
}

function ViewEventRow({
  event,
  expenseCurrency,
  onEdit,
  onDelete,
}: {
  event: ReimbursementEvent;
  expenseCurrency: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-foreground">
            {formatCurrency(event.amount, expenseCurrency)}
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="text-foreground">{formatDate(event.paidDate)}</span>
          {event.paymentMethod && (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground capitalize">
                {event.paymentMethod.replace(/_/g, " ")}
              </span>
            </>
          )}
        </div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          <span className="font-mono">{event.code}</span>
          {event.recordedByName && <> · recorded by {event.recordedByName}</>}
        </div>
        {event.notes && (
          <div className="mt-1 whitespace-pre-wrap text-[12px] text-foreground">
            {event.notes}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          title="Edit"
        >
          <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(0,70%,96%)] hover:text-[hsl(0,70%,45%)]"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

type DropdownItem = { id: string; label: string; value: string };

function EditEventRow({
  event,
  paymentItems,
  onCancel,
  onSave,
  saving,
  error,
}: {
  event: ReimbursementEvent;
  expenseCurrency: string;
  paymentItems: DropdownItem[];
  onCancel: () => void;
  onSave: (payload: {
    amount: number;
    paidDate: string;
    paymentMethod: string | null;
    notes: string | null;
  }) => Promise<void>;
  saving: boolean;
  error: string | null;
}) {
  const [amount, setAmount] = useState(String(event.amount));
  const [paidDate, setPaidDate] = useState(event.paidDate.slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState(event.paymentMethod ?? "");
  const [notes, setNotes] = useState(event.notes ?? "");

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <Input
          type="date"
          value={paidDate}
          onChange={(e) => setPaidDate(e.target.value)}
        />
        <Select
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
        >
          <option value="">Not specified</option>
          {paymentItems.map((p) => (
            <option key={p.id} value={p.value}>
              {p.label}
            </option>
          ))}
        </Select>
      </div>
      <textarea
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      {error && (
        <div className="text-[12px] text-[hsl(0,70%,45%)]">{error}</div>
      )}
      <div className="flex items-center justify-end gap-1">
        <button
          type="button"
          onClick={() =>
            onSave({
              amount: Number(amount),
              paidDate: new Date(paidDate).toISOString(),
              paymentMethod: paymentMethod || null,
              notes: notes.trim() || null,
            })
          }
          disabled={saving}
          className="flex h-7 w-7 items-center justify-center rounded text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)]"
          title="Save"
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
          ) : (
            <Check className="h-3.5 w-3.5" strokeWidth={1.8} />
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          title="Cancel"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

function RecordDialog({
  expenseId,
  expenseCurrency,
  owed,
  paymentItems,
  onClose,
  onSubmit,
  saving,
  error,
}: {
  expenseId: string;
  expenseCurrency: string;
  owed: number;
  paymentItems: DropdownItem[];
  onClose: () => void;
  onSubmit: (payload: {
    expenseId: string;
    amount: number;
    paidDate: string;
    paymentMethod: string | null;
    notes: string | null;
  }) => Promise<void>;
  saving: boolean;
  error: string | null;
}) {
  const [amount, setAmount] = useState(owed > 0 ? owed.toFixed(2) : "");
  const [paidDate, setPaidDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  function submit() {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setLocalError("Amount must be greater than zero");
      return;
    }
    if (!paidDate) {
      setLocalError("Paid date is required");
      return;
    }
    setLocalError(null);
    onSubmit({
      expenseId,
      amount: amt,
      paidDate: new Date(paidDate).toISOString(),
      paymentMethod: paymentMethod || null,
      notes: notes.trim() || null,
    });
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
      <div
        className="w-[440px] rounded-xl border bg-white p-6 shadow-xl"
        style={{ borderColor: BORDER }}
      >
        <h3 className="text-[15px] font-semibold text-foreground">
          Record Reimbursement
        </h3>
        <p className="mt-1 text-[12px] text-muted-foreground">
          {owed > 0
            ? `Outstanding: ${formatCurrency(owed, expenseCurrency)}`
            : "Already fully reimbursed."}
        </p>
        <div className="mt-4 space-y-3">
          <div className="space-y-1">
            <Label>
              Amount <span className="text-destructive">*</span>
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label>
              Paid Date <span className="text-destructive">*</span>
            </Label>
            <Input
              type="date"
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Payment Method</Label>
            <Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="">Not specified</option>
              {paymentItems.map((p) => (
                <option key={p.id} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Bank transfer ref ABC123"
              className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          {(localError || error) && (
            <div
              className="rounded-md border px-3 py-2 text-[12px]"
              style={{
                borderColor: "hsl(0,70%,80%)",
                background: "hsl(0,70%,96%)",
                color: "hsl(0,70%,35%)",
              }}
            >
              {localError ?? error}
            </div>
          )}
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving && (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
            )}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
